import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, fmt, fmtDate, formatApiError, todayISO } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  ClipboardList, Plus, RefreshCcw, Search, Edit2, Trash2, X, Save, CheckCircle2,
  AlertCircle, HelpCircle,
} from "lucide-react";

const STATUS_CONFIG = {
  matched:   { cls: "bg-[#DCFCE7] text-[#15803D]",   label: "Matched",   icon: CheckCircle2 },
  mismatch:  { cls: "bg-[#FEE2E2] text-[#B91C1C]",   label: "Mismatch",  icon: AlertCircle },
  unmatched: { cls: "bg-[#FEF3C7] text-[#B45309]",   label: "Unmatched", icon: HelpCircle },
};

const KIND_FILTERS  = [{ key: "all", label: "All" }, { key: "sale", label: "Sales" }, { key: "purchase", label: "Purchases" }];
const STATUS_FILTERS = [
  { key: "all",       label: "All" },
  { key: "matched",   label: "Matched" },
  { key: "mismatch",  label: "Mismatch" },
  { key: "unmatched", label: "Unmatched" },
];

const EMPTY_FORM = () => ({
  date: todayISO(), kind: "sale", party_name: "", party_id: "",
  challan_no: "", notes: "", items: [{ brand: "", size: "", qty: 0 }],
});

function EntryForm({ initial, customers, masters, onSave, onClose }) {
  const [form, setForm] = useState(initial || EMPTY_FORM());
  const [busy, setBusy] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem  = (i, k, v) => setForm((f) => {
    const items = [...f.items];
    items[i] = { ...items[i], [k]: v };
    return { ...f, items };
  });
  const addItem    = () => setForm((f) => ({ ...f, items: [...f.items, { brand: "", size: "", qty: 0 }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.kind) { toast.error("Date and kind are required"); return; }
    setBusy(true);
    try {
      await onSave(form);
    } finally { setBusy(false); }
  };

  const brands   = (masters?.brands || []).map((b) => b.name || b);
  const sizes    = (masters?.sizes  || []).map((s) => s.name || s);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-xl max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="register-form">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-heading font-black text-lg">{initial?.id ? "Edit Entry" : "New Register Entry"}</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="small-label block mb-1.5">Date</label>
              <input type="date" required className="input-lg" value={form.date}
                onChange={(e) => setField("date", e.target.value)} data-testid="reg-date" />
            </div>
            <div>
              <label className="small-label block mb-1.5">Kind</label>
              <select className="input-lg" value={form.kind} onChange={(e) => setField("kind", e.target.value)}
                data-testid="reg-kind">
                <option value="sale">Sale (outward)</option>
                <option value="purchase">Purchase (inward)</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="small-label block mb-1.5">Party name</label>
              <input className="input-lg" list="reg-party-list" value={form.party_name}
                onChange={(e) => setField("party_name", e.target.value)} placeholder="Customer / vendor name"
                data-testid="reg-party" />
              <datalist id="reg-party-list">
                {(customers || []).map((c) => <option key={c.id} value={c.name} />)}
              </datalist>
            </div>
            <div>
              <label className="small-label block mb-1.5">Challan No</label>
              <input className="input-lg font-mono" value={form.challan_no}
                onChange={(e) => setField("challan_no", e.target.value)}
                placeholder="e.g. 1042" data-testid="reg-challan" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="small-label">Items in register</label>
              <button type="button" onClick={addItem} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                <Plus size={12} /> Add row
              </button>
            </div>
            <div className="space-y-2">
              {form.items.map((it, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`reg-item-${i}`}>
                  <input list={`reg-brand-list-${i}`} value={it.brand}
                    onChange={(e) => setItem(i, "brand", e.target.value)}
                    placeholder="Brand" className="input-lg flex-1 text-sm"
                    data-testid={`reg-item-brand-${i}`} />
                  <datalist id={`reg-brand-list-${i}`}>
                    {brands.map((b) => <option key={b} value={b} />)}
                  </datalist>
                  <input list={`reg-size-list-${i}`} value={it.size}
                    onChange={(e) => setItem(i, "size", e.target.value)}
                    placeholder="Size" className="input-lg flex-1 text-sm"
                    data-testid={`reg-item-size-${i}`} />
                  <datalist id={`reg-size-list-${i}`}>
                    {sizes.map((s) => <option key={s} value={s} />)}
                  </datalist>
                  <input type="number" min={0} value={it.qty}
                    onChange={(e) => setItem(i, "qty", parseInt(e.target.value) || 0)}
                    className="input-lg w-20 text-sm" placeholder="Qty"
                    data-testid={`reg-item-qty-${i}`} />
                  {form.items.length > 1 && (
                    <button type="button" onClick={() => removeItem(i)}
                      className="text-gray-400 hover:text-red-500">
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="small-label block mb-1.5">Notes</label>
            <textarea className="input-lg min-h-[60px]" value={form.notes}
              onChange={(e) => setField("notes", e.target.value)} data-testid="reg-notes" />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button type="submit" disabled={busy}
              className="btn-primary flex items-center gap-1.5"
              data-testid="reg-submit">
              <Save size={14} /> {initial?.id ? "Update" : "Save Entry"}
            </button>
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PhysicalRegisterPage() {
  const { auth } = useAuth();
  const [rows, setRows]         = useState([]);
  const [summary, setSummary]   = useState({});
  const [loading, setLoading]   = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [kindFilter, setKindFilter]     = useState("all");
  const [search, setSearch]             = useState("");
  const [showForm, setShowForm]         = useState(false);
  const [editing, setEditing]           = useState(null);
  const [customers, setCustomers]       = useState([]);
  const [masters, setMasters]           = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/register-entries", {
        params: { status: statusFilter, kind: kindFilter === "all" ? undefined : kindFilter },
      });
      setRows(data.rows || []);
      setSummary(data.summary || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [statusFilter, kindFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const [c, m] = await Promise.all([
          api.get("/customers?customer_type=customer"),
          api.get("/masters"),
        ]);
        setCustomers(c.data || []);
        setMasters(m.data || {});
      } catch { /* ignore */ }
    })();
  }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) => (r.party_name || "").toLowerCase().includes(q)
          || (r.challan_no || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const saveEntry = async (form) => {
    try {
      if (editing?.id) {
        await api.patch(`/register-entries/${editing.id}`, form);
        toast.success("Entry updated");
      } else {
        await api.post("/register-entries", form);
        toast.success("Entry saved");
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Delete this register entry?")) return;
    try {
      await api.delete(`/register-entries/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (row) => {
    setEditing(row);
    setShowForm(true);
  };

  return (
    <div className="space-y-6" data-testid="physical-register-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ClipboardList size={22} className="text-black" />
          <h2 className="font-heading font-black text-2xl tracking-tight">Physical Register</h2>
        </div>
        <span className="text-xs text-gray-500 border border-gray-200 rounded-sm px-2 py-0.5">
          Compare physical book entries with system challans
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={load}
            className="h-9 px-3 border border-gray-300 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 hover:border-black"
            data-testid="reg-refresh"><RefreshCcw size={13} /> Refresh</button>
          <button onClick={() => { setEditing(null); setShowForm(true); }}
            className="btn-primary h-9 px-3 text-xs flex items-center gap-1.5"
            data-testid="reg-add"><Plus size={14} /> Add Entry</button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-3 lg:grid-cols-4 gap-4">
        <div className="card-flat p-4">
          <div className="small-label text-gray-500">Total entries</div>
          <div className="data-num text-3xl mt-1" data-testid="reg-total">{summary.total ?? "—"}</div>
        </div>
        <div className="card-flat p-4 bg-[#DCFCE7] border-[#BBF7D0]">
          <div className="small-label text-[#15803D]">Matched</div>
          <div className="data-num text-3xl mt-1 text-[#15803D]" data-testid="reg-matched">{summary.matched ?? "—"}</div>
        </div>
        <div className="card-flat p-4 bg-[#FEE2E2] border-[#FECACA]">
          <div className="small-label text-[#B91C1C]">Mismatch</div>
          <div className="data-num text-3xl mt-1 text-[#B91C1C]" data-testid="reg-mismatch">{summary.mismatch ?? "—"}</div>
        </div>
        <div className="card-flat p-4 bg-[#FEF3C7] border-[#FDE68A]">
          <div className="small-label text-[#B45309]">Unmatched</div>
          <div className="data-num text-3xl mt-1 text-[#B45309]" data-testid="reg-unmatched">{summary.unmatched ?? "—"}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap card-flat p-3">
        {STATUS_FILTERS.map((f) => (
          <button key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={`px-3 h-8 rounded-sm text-xs font-semibold border ${statusFilter === f.key ? "bg-black text-white border-black" : "bg-white text-gray-600 border-gray-300 hover:border-black"}`}
            data-testid={`reg-status-${f.key}`}>{f.label}</button>
        ))}
        <div className="h-6 w-px bg-gray-200 mx-1" />
        {KIND_FILTERS.map((f) => (
          <button key={f.key}
            onClick={() => setKindFilter(f.key)}
            className={`px-3 h-8 rounded-sm text-xs font-semibold border ${kindFilter === f.key ? "border-black bg-gray-50" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}
            data-testid={`reg-kind-${f.key}`}>{f.label}</button>
        ))}
        <div className="ml-auto relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search party / challan…"
            className="input-lg h-9 pl-8 w-48 text-sm" data-testid="reg-search" />
        </div>
      </div>

      {/* Table */}
      <div className="card-flat" data-testid="reg-table">
        <div className="table-wrap">
          <table className="table-dense w-full">
            <thead>
              <tr>
                <th className="text-left">Date</th>
                <th className="text-left">Kind</th>
                <th className="text-left">Party</th>
                <th className="text-left">Challan</th>
                <th className="text-left">Items</th>
                <th className="text-left">Status</th>
                <th className="text-left">Diff</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="py-8 text-center text-sm text-gray-400">Loading…</td></tr>}
              {!loading && visible.length === 0 && (
                <tr><td colSpan={8} className="py-8 text-center text-sm text-gray-400">No entries found.</td></tr>
              )}
              {!loading && visible.map((r) => {
                const st = STATUS_CONFIG[r.status] || STATUS_CONFIG.unmatched;
                const Icon = st.icon;
                return (
                  <tr key={r.id} data-testid={`reg-row-${r.id}`}>
                    <td className="text-xs text-gray-500">{fmtDate(r.date)}</td>
                    <td>
                      <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-sm ${r.kind === "sale" ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-700"}`}>
                        {r.kind === "sale" ? "Sale" : "Purchase"}
                      </span>
                    </td>
                    <td className="font-semibold text-sm">{r.party_name || "—"}</td>
                    <td className="font-mono text-xs">{r.challan_no || "—"}</td>
                    <td className="text-xs text-gray-600">
                      {(r.items || []).map((it, i) => (
                        <span key={i} className="inline-block bg-gray-100 rounded-sm px-1.5 py-0.5 mr-1 mb-1">
                          {it.brand} {it.size} × {it.qty}
                        </span>
                      ))}
                    </td>
                    <td>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-sm ${st.cls}`}>
                        <Icon size={10} /> {st.label}
                      </span>
                    </td>
                    <td className="text-xs">
                      {(r.item_diffs || []).length > 0 ? (
                        <span className="text-[#B91C1C] font-semibold">
                          {r.item_diffs.map((d, i) => (
                            <div key={i}>{d.brand} {d.size}: Reg {d.register_qty} vs Sys {d.txn_qty}</div>
                          ))}
                        </span>
                      ) : r.status === "matched" ? (
                        <span className="text-[#15803D] text-[11px]">No difference</span>
                      ) : r.status === "unmatched" ? (
                        <span className="text-gray-400 text-[11px]">No challan found</span>
                      ) : "—"}
                    </td>
                    <td className="text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => openEdit(r)}
                          className="h-7 px-2 border border-gray-300 rounded-sm text-xs hover:border-black"
                          data-testid={`reg-edit-${r.id}`}>
                          <Edit2 size={12} />
                        </button>
                        <button onClick={() => deleteEntry(r.id)}
                          className="h-7 px-2 border border-gray-200 rounded-sm text-xs hover:border-red-400 hover:text-red-500"
                          data-testid={`reg-del-${r.id}`}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <EntryForm
          initial={editing}
          customers={customers}
          masters={masters}
          onSave={saveEntry}
          onClose={() => { setShowForm(false); setEditing(null); }}
        />
      )}
    </div>
  );
}
